import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Tabs, Tab, Typography, CircularProgress, Autocomplete, TextField, } from "@mui/material";
import FDialog from "@ui/components/dialog/FDialog";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { getNodeTemperatureColor } from "@ui/components/temperature";
import { useTranslation } from "react-i18next";
import { SButton } from "@ui/components/buttons/SButton";
import { Icon } from "@iconify/react";
import { Electricity } from "iconsax-react";
import { useAppDispatch } from "@application/store/useStore";
import { getReplacementCandidates } from "@reducers/nodes/thunk/nodes-thunk";
const PAGE_SIZE = 20;
export const NodeActionsDialog = ({ isOpen, onClose, onConfirm, selectedNode, action = "disconnect", showReplaceTab = true, onReplace, }) => {
    const { t } = useTranslation(["common"]);
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState("disconnect");
    const [selectedReplacement, setSelectedReplacement] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [inputValue, setInputValue] = useState("");
    const searchTimerRef = useRef(null);
    // Reset all candidate state when dialog opens or closes
    useEffect(() => {
        if (isOpen) {
            setActiveTab("disconnect");
            setSelectedReplacement(null);
            setCandidates([]);
            setTotalCount(0);
            setPage(1);
            setDebouncedSearch("");
            setInputValue("");
        }
    }, [isOpen]);
    // Fetch candidates whenever the replace tab is active, or search/page changes
    useEffect(() => {
        if (!isOpen || !showReplaceTab || activeTab !== "replace")
            return;
        let cancelled = false;
        setLoading(true);
        dispatch(getReplacementCandidates({
            search: debouncedSearch || null,
            pageNumber: page,
            pageSize: PAGE_SIZE,
        }))
            .unwrap()
            .then((result) => {
            var _a, _b;
            if (cancelled)
                return;
            const filtered = ((_a = result.items) !== null && _a !== void 0 ? _a : []).filter((n) => n.id !== (selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.id));
            // page === 1 means new search/first load — replace; page > 1 — append
            setCandidates((prev) => (page === 1 ? filtered : [...prev, ...filtered]));
            setTotalCount((_b = result.totalCount) !== null && _b !== void 0 ? _b : 0);
        })
            .catch(() => {
            if (!cancelled)
                setCandidates((prev) => (page === 1 ? [] : prev));
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, showReplaceTab, activeTab, debouncedSearch, page, dispatch, selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.id]);
    // Handle all three MUI onInputChange reasons explicitly.
    // "reset": fired when options update, an option is selected, or the dropdown opens —
    //          MUI would overwrite the displayed text if inputValue is uncontrolled, so we
    //          take control and simply ignore it to keep the user's typed text in place.
    // "clear": user clicked the clear (×) button — reset search and inputValue.
    // "input": user actually typed — debounce and fire a new search.
    const handleInputChange = useCallback((_, value, reason) => {
        if (reason === "reset")
            return;
        if (reason === "clear") {
            setInputValue("");
            if (searchTimerRef.current)
                clearTimeout(searchTimerRef.current);
            setPage(1);
            setDebouncedSearch("");
            return;
        }
        // reason === "input"
        setInputValue(value);
        if (searchTimerRef.current)
            clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setPage(1);
            setDebouncedSearch(value);
        }, 300);
    }, []);
    const handleListboxScroll = useCallback((event) => {
        const el = event.currentTarget;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        if (nearBottom && !loading && candidates.length < totalCount) {
            setPage((p) => p + 1);
        }
    }, [loading, candidates.length, totalCount]);
    const handleReplace = () => {
        if (selectedReplacement && onReplace) {
            onReplace(selectedReplacement.id);
        }
    };
    const handleClose = () => {
        setActiveTab("disconnect");
        onClose();
    };
    const isConnect = action === "connect";
    const NodeCard = () => {
        var _a, _b;
        return selectedNode ? (React.createElement(Box, { sx: {
                p: 2.5,
                border: "1px solid #DFE3E8",
                borderRadius: 4,
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                gap: 2,
            } },
            React.createElement(TemperatureDisplay, { temperature: (_b = (_a = selectedNode.nodeTemperature) !== null && _a !== void 0 ? _a : selectedNode.temperature) !== null && _b !== void 0 ? _b : null, color: selectedNode.nodeTypeCode === 1 ? "#1D95E2" : getNodeTemperatureColor(selectedNode), width: 91 }),
            React.createElement(Box, { sx: { flex: 1 } },
                React.createElement(Typography, { variant: "subtitle1", fontWeight: 600 }, selectedNode.nodeName),
                React.createElement(Typography, { variant: "subtitle1", fontWeight: 400 }, selectedNode.nodeId)))) : null;
    };
    const actions = activeTab === "disconnect" ? (React.createElement(React.Fragment, null,
        React.createElement(SButton, { variant: "soft", size: "medium", onClick: handleClose }, t("common:actions.cancel")),
        React.createElement(SButton, { variant: "contained", size: "medium", onClick: onConfirm }, isConnect
            ? t("common:actions.connectNode")
            : t("common:actions.disconnectNode")))) : (React.createElement(React.Fragment, null,
        React.createElement(SButton, { variant: "soft", size: "medium", onClick: handleClose }, t("common:actions.cancel")),
        React.createElement(SButton, { variant: "contained", size: "medium", onClick: handleReplace, disabled: !selectedReplacement || loading }, t("common:actions.replaceNode"))));
    return (React.createElement(FDialog, { isOpen: isOpen, onClose: handleClose, onSubmit: activeTab === "disconnect" ? onConfirm : handleReplace, title: t("common:labels.nodeActions"), disableActions: true, maxWidth: "sm" },
        showReplaceTab ? (React.createElement(Tabs, { value: activeTab, onChange: (_, v) => setActiveTab(v), sx: { mb: 2, minHeight: 36 } },
            React.createElement(Tab, { value: "disconnect", label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 } },
                    React.createElement(Electricity, null),
                    React.createElement("span", null, isConnect
                        ? t("common:actions.connect")
                        : t("common:actions.disconnect"))) }),
            React.createElement(Tab, { value: "replace", label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 } },
                    React.createElement(Icon, { icon: "tdesign:refresh" }),
                    React.createElement("span", null, t("common:actions.replaceNode"))) }))) : (React.createElement(Box, { sx: { mb: 2 } },
            React.createElement(Typography, { variant: "subtitle2", color: "text.secondary" }, isConnect
                ? t("common:actions.connect")
                : t("common:actions.disconnect")))),
        activeTab === "disconnect" ? (React.createElement(Box, { sx: { mb: 2 } },
            React.createElement(NodeCard, null))) : (React.createElement(Box, { sx: { mb: 2 } },
            React.createElement(Autocomplete, { options: candidates, value: selectedReplacement, inputValue: inputValue, onChange: (_, value) => {
                    setSelectedReplacement(value);
                    setInputValue(value ? `${value.nodeName || value.nodeUid} (${value.nodeUid})` : "");
                }, onInputChange: handleInputChange, getOptionLabel: (o) => `${o.nodeName || o.nodeUid} (${o.nodeUid})`, isOptionEqualToValue: (a, b) => a.id === b.id, filterOptions: (x) => x, loading: loading, noOptionsText: loading
                    ? t("common:status.loading")
                    : t("common:messages.noReplacementCandidates"), ListboxProps: { onScroll: handleListboxScroll }, renderInput: (params) => (React.createElement(TextField, Object.assign({}, params, { label: t("common:labels.replacementNode"), InputProps: Object.assign(Object.assign({}, params.InputProps), { endAdornment: (React.createElement(React.Fragment, null,
                            loading ? (React.createElement(CircularProgress, { size: 18, sx: { mr: 1 } })) : null,
                            params.InputProps.endAdornment)) }) }))) }),
            React.createElement(Box, { sx: { mt: 1.5 } },
                React.createElement(NodeCard, null)))),
        React.createElement(Box, { sx: { display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 } }, actions)));
};
